/**
 * @fileoverview The gift's thank-you page, /gift/thanks?session_id=cs_...: where Stripe
 * Checkout sends the buyer once they have paid. Public.
 *
 * It shows the gift code, large, with the redeem link; Share and Copy link
 * for the link, as every link in Grow is shared (ShareButtons), then Copy
 * code and Print; and
 * says what happens next: "We'll email Ben on December 25 with your
 * message", "We're emailing Ben now", or, with no recipient email, "Give the
 * code to Ben however you like". A receipt with the code goes to the buyer
 * too. While the payment is still going through it says so and asks again
 * every few seconds, for a while; a request that fails meanwhile is asked
 * again too. When the while is up it says the receipt will bring the code
 * (gift.thanks.failed), where it used to keep "in a moment" spinning.
 * When the last ask failed on our side (a 500, no connection; D31,
 * lib/serverFailure), that sentence stays (the payment is safe and the code
 * is on its way) and the standard sentence follows it, reported to Sentry,
 * with the support address as a link.
 *
 * Where: the route /gift/thanks?session_id= (App.tsx), Stripe Checkout's
 * success_url for a gift; public, in GiftShell.
 * API: GET /gifts/purchased?session_id= (getPurchasedGift), asked again
 * every POLL_MS up to POLL_TIMES while it answers 'pending'.
 * Words (en/es): gift.thanks.*, gift.redeem.title (the shared link's title),
 * share.actions.* (Share, Copy link), sitewide.loading.
 * Watch out: "We'll email Ben on…" is said only when the server says the
 * email is scheduled (gift.scheduled: its moment more than an hour away by
 * the server's clock); anything sooner reads as "now". The browser's clock
 * decides nothing here.
 *
 * @module GiftThanksPage
 */

import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Copy, Printer } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import Spinner from '@/shared/components/ui/Spinner';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { isServerFailure } from '@/shared/lib/serverFailure';
import { ServerFailureNote } from '@/shared/components/ServerFailure';
import { getPurchasedGift, type PurchasedGift } from '@/shared/api/gifts';
import { formatPrice } from '@/features/billing/lib/useBilling';
import { GiftShell } from './components/GiftShell';
import { CopyLinkButton, ShareError, ShareLinkButton } from '@/features/share/ShareButtons';
import { SHARE_BUTTON_CLASS, canShareLink } from '@/features/share/lib/shareLink';
import { COPIED_RESET_MS } from '@/config/tunables/ui';
import { useGiftLanguage } from './lib/useGiftLanguage';
import { giftDay } from './lib/giftCode';

/** How often to ask again while the payment goes through, and how many times. */
const POLL_MS = 3000;
/** How many times to ask before saying the receipt will bring the code. */
const POLL_TIMES = 10;

/** What the page shows; a failure with what the last ask threw, if it threw. */
type Shown = { kind: 'loading' } | { kind: 'pending' } | { kind: 'ready'; gift: PurchasedGift } | { kind: 'failed'; error?: unknown };

/** The page at /gift/thanks. */
export const GiftThanksPage: React.FC = () => {
  const { reactTranslate: t } = useTranslation();
  const language = useGiftLanguage();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id') ?? '';
  const [shown, setShown] = React.useState<Shown>({ kind: 'loading' });
  /** The code was just copied: "Copied" for a moment. */
  const [copied, setCopied] = React.useState(false);
  /** The browser would not share or copy the redeem link: what to do instead. */
  const [linkError, setLinkError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    let asked = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // What the last ask threw, if it did; an answer clears it.
    let lastError: unknown;
    /** Asks again in a moment, or, once the asks are spent, says the receipt will bring it. */
    const later = (): void => {
      if (asked < POLL_TIMES) timer = setTimeout(ask, POLL_MS);
      else setShown({ kind: 'failed', error: lastError });
    };
    /** Asks for the gift; again later while the payment is still going through. */
    const ask = (): void => {
      asked += 1;
      getPurchasedGift(sessionId)
        .then((answer) => {
          if (cancelled) return;
          lastError = undefined;
          if (answer.status === 'ready') setShown({ kind: 'ready', gift: answer.gift });
          else {
            setShown({ kind: 'pending' });
            later();
          }
        })
        .catch((error) => {
          if (cancelled) return;
          lastError = error;
          // The first ask failing is said at once; one during the wait is a blip, asked again.
          if (asked === 1) setShown({ kind: 'failed', error });
          else later();
        });
    };
    if (sessionId) ask();
    else setShown({ kind: 'failed' });
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [sessionId]);

  /**
   * Copies the code, and says so for a moment (COPIED_RESET_MS). Where the
   * browser refuses, the code is on the page, large, to copy by hand.
   *
   * @param text - The code.
   */
  const copy = async (text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), COPIED_RESET_MS);
    } catch { /* the code is on the page to copy by hand */ }
  };

  /**
   * What happens next, in one sentence.
   *
   * @param gift - The gift bought.
   */
  const next = (gift: PurchasedGift): string => {
    const name = gift.recipientName || t('gift.thanks.them');
    if (!gift.recipientEmail) return t('gift.thanks.byHand', { name });
    return gift.scheduled && gift.sendOn ? t('gift.thanks.scheduled', { name, date: giftDay(gift.sendOn, language) }) : t('gift.thanks.today', { name });
  };

  return (
    <GiftShell>
      {shown.kind === 'loading' && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner size={16} />{t('sitewide.loading')}</div>
      )}
      {shown.kind === 'pending' && (
        <>
          <h1 className="font-grow text-3xl font-bold leading-tight">{t('gift.thanks.title')}</h1>
          <p className="mt-3 flex items-center gap-2 text-muted-foreground"><Spinner size={16} />{t('gift.thanks.pending')}</p>
        </>
      )}
      {shown.kind === 'failed' && (
        <>
          <h1 className="font-grow text-3xl font-bold leading-tight">{t('gift.thanks.title')}</h1>
          <p className="mt-3 text-muted-foreground">{t('gift.thanks.failed')}</p>
          {isServerFailure(shown.error) && (
            <ServerFailureNote error={shown.error} where="Gift thanks" className="mt-3 text-base text-muted-foreground" />
          )}
        </>
      )}
      {shown.kind === 'ready' && (
        <div className="print:text-black">
          <h1 className="font-grow text-3xl font-bold leading-tight">{t('gift.thanks.title')}</h1>
          <p className="mt-3">{next(shown.gift)}</p>

          <div className="mt-6 rounded-md border-2 border-dashed border-primary bg-card px-4 py-5 text-center">
            <p className="text-sm text-muted-foreground">{t('gift.thanks.codeLabel')}</p>
            <p className="mt-1 break-all font-mono text-2xl font-bold tracking-widest text-primary sm:text-3xl">{shown.gift.code}</p>
            <p className="mt-3 break-all text-sm">{shown.gift.redeemUrl}</p>
          </div>

          {/* The redeem link is shared as every link in Grow is (ShareButtons): Share and Copy link; then the code and Print. */}
          <div className="mt-4 flex flex-wrap gap-2 print:hidden">
            <ShareLinkButton link={shown.gift.redeemUrl} title={t('gift.redeem.title')} onError={setLinkError} />
            <CopyLinkButton link={shown.gift.redeemUrl} onError={setLinkError} variant={canShareLink() ? 'outline' : 'default'} />
            <Button variant="outline" className={SHARE_BUTTON_CLASS} onClick={() => { void copy(shown.gift.code); }}>
              <Copy className="mr-2 h-4 w-4" aria-hidden="true" />
              {copied ? t('gift.thanks.copied') : t('gift.thanks.copyCode')}
            </Button>
            <Button variant="outline" className={SHARE_BUTTON_CLASS} onClick={() => window.print()}>
              <Printer className="mr-2 h-4 w-4" aria-hidden="true" />
              {t('gift.thanks.print')}
            </Button>
          </div>
          {linkError && <div className="mt-2 print:hidden"><ShareError text={linkError} /></div>}

          <ul className="mt-8 space-y-1 text-sm text-muted-foreground">
            <li>{t('gift.thanks.receipt')}</li>
            <li>{t('gift.thanks.paid', { price: formatPrice({ amount: shown.gift.amount, currency: shown.gift.currency }, language === 'es' ? 'es-US' : 'en-US') })}</li>
            <li>{t('gift.thanks.once')}</li>
          </ul>
        </div>
      )}
    </GiftShell>
  );
};

export default GiftThanksPage;
